import React, { Suspense } from 'react'
import { HashRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import GoogleAnalyticsReporter from '../components/analytics/GoogleAnalyticsReporter'
import Header from '../components/Header'
import Popups from '../components/Popups'
import Web3ReactManager from '../components/Web3ReactManager'
import DarkModeQueryParamReader from '../theme/DarkModeQueryParamReader'
import AddLiquidity from './AddLiquidity'
import {
  RedirectDuplicateTokenIds,
  RedirectOldAddLiquidityPathStructure,
  RedirectToAddLiquidity
} from './AddLiquidity/redirects'
// import MigrateV1 from './MigrateV1'
// import MigrateV1Exchange from './MigrateV1/MigrateV1Exchange'
import RemoveV1Exchange from './MigrateV1/RemoveV1Exchange'
import Pool from './Pool'
import Portfolio from './Portfolio'
import PoolFinder from './PoolFinder'
import RemoveLiquidity from './RemoveLiquidity'
import { RedirectOldRemoveLiquidityPathStructure } from './RemoveLiquidity/redirects'
import Swap from './Swap'
import { RedirectPathToSwapOnly, RedirectToSwap } from './Swap/redirects'
import Bridge from './Bridge'
import Landing from './Landing'
import FishComponent from '../components/FishComponent'
import { Footer } from '../components/Footer'
import { usesEcosystemTheme } from '../utils/ecosystemTheme'
import { landingSearchForTab } from '../utils/landingWidgetTab'

const AppWrapper = styled.div`
  display: flex;
  position: relative;
  flex-flow: column;
  align-items: flex-start;
  overflow-x: hidden;
  min-height: 100vh;
  background-color: #fff;
`

const HeadBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const HeaderWrapper = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  justify-content: space-between;
`

const BodyWrapper = styled.div<{ $landing?: boolean; $portfolio?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: ${({ $landing, $portfolio }) => ($portfolio ? '96px' : $landing ? '88px' : '120px')};
  align-items: ${({ $portfolio }) => ($portfolio ? 'stretch' : 'center')};
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 10;
  min-height: calc(100vh - 120px);

  z-index: 1;

  ${({ theme, $landing, $portfolio }) => theme.mediaWidth.upToSmall`
    padding: ${$portfolio ? '88px' : $landing ? '68px' : '96px'} 12px 24px;
  `};

  ${({ theme, $landing, $portfolio }) => theme.mediaWidth.upToMedium`
    padding-top: ${$portfolio ? '92px' : $landing ? '88px' : '104px'};
  `};

  .fish-bottom-right {
    position: fixed;
    bottom: 0px;
    right: max(-80px, -10vw);
    width: 40vw;
    max-width: 350px;
    pointer-events: none;
    user-select: none;
    opacity: 0.3;
    z-index: 0;
  }

  @media (max-width: 1024px) {
    .fish-bottom-right {
      bottom: 0px;
    }
  }
`

function AppRoutes() {
  const { pathname } = useLocation()
  const isLanding = pathname === '/'
  const isPortfolio = pathname === '/portfolio'
  const ecosystemTheme = usesEcosystemTheme(pathname)

  return (
    <BodyWrapper $landing={isLanding || ecosystemTheme} $portfolio={isPortfolio}>
      <Popups />
      <AppBg $ecosystem={ecosystemTheme} />
      {ecosystemTheme ? <LandingHeroGlow /> : <SeamlessGradient />}

      <Web3ReactManager>
        <Switch>
          <Route exact strict path="/" component={Landing} />
          <Route
            exact
            strict
            path="/swap"
            render={({ location }) => (
              <Redirect to={{ ...location, pathname: '/', search: landingSearchForTab('swap', location.search) }} />
            )}
          />
          <Route exact strict path="/swap/:outputCurrency" component={RedirectToSwap} />
          <Route exact strict path="/send" component={RedirectPathToSwapOnly} />
          <Route exact strict path="/find" component={PoolFinder} />
          <Route exact strict path="/pool" component={Pool} />
          <Route exact strict path="/portfolio" component={Portfolio} />
          <Route
            exact
            strict
            path="/bridge"
            render={({ location }) => (
              <Redirect to={{ ...location, pathname: '/', search: landingSearchForTab('bridge', location.search) }} />
            )}
          />
          <Route exact strict path="/create" component={RedirectToAddLiquidity} />
          <Route exact path="/add" component={AddLiquidity} />
          <Route exact path="/add/:currencyIdA" component={RedirectOldAddLiquidityPathStructure} />
          <Route exact path="/add/:currencyIdA/:currencyIdB" component={RedirectDuplicateTokenIds} />
          <Route exact strict path="/remove/v1/:address" component={RemoveV1Exchange} />
          <Route exact strict path="/remove/:tokens" component={RedirectOldRemoveLiquidityPathStructure} />
          <Route exact strict path="/remove/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />
          {/* <Route exact strict path="/migrate/v1" component={MigrateV1} />
          <Route exact strict path="/migrate/v1/:address" component={MigrateV1Exchange} /> */}
          <Route component={RedirectPathToSwapOnly} />
        </Switch>
      </Web3ReactManager>

      <FishComponent />

      {ecosystemTheme && !isLanding ? (
        <EcosystemFooterWrap>
          <Footer />
        </EcosystemFooterWrap>
      ) : null}
    </BodyWrapper>
  )
}

export default function App() {
  return (
    <Suspense fallback={null}>
      <HashRouter>
        <Route component={GoogleAnalyticsReporter} />
        <Route component={DarkModeQueryParamReader} />
        <AppWrapper>
          <HeadBodyWrapper>
            <HeaderWrapper>
              <Header />
            </HeaderWrapper>
            <AppRoutes />
          </HeadBodyWrapper>
        </AppWrapper>
      </HashRouter>
    </Suspense>
  )
}

const AppBg = styled.div<{ $ecosystem?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ $ecosystem }) => ($ecosystem ? '#f6f3ec' : '#fff')};
  overflow: hidden;
`

const LandingHeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 520px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40px;
    left: -10%;
    width: 450px;
    height: 450px;
    border-radius: 50%;
    opacity: 0.4;
    filter: blur(64px);
    background: linear-gradient(135deg, #1faeff, #043f83);
  }
`

const EcosystemFooterWrap = styled.div`
  width: 100%;
  align-self: stretch;
  margin-top: auto;
  z-index: 2;
  padding-top: 32px;
`

const SeamlessGradient = styled.div`
  top: 73px;
  background: linear-gradient(180deg, #034e94 0%, #75b6fe 59.62%, #ffffff 100%);
  width: 100%;
  height: 500px;
  left: 0px;
  position: absolute;
`
